import { useEffect, useState } from 'react'
import { format } from 'date-fns'
import { CountCardContainer } from '../../components/wrapper/CountCardsContainer'
import Icon from '../../components/icon'
import { TabNav } from '../../components/nav/tabNav'
import Rating from '../../components/star'
import { useGetAllRatingsQuery } from '../../redux-toolkits/settings/settings.slice'
import { capitalize } from '@mui/material'
import { QueryProps } from '../../redux-toolkits/shared/shared.type'
import { useSearchParams } from 'react-router-dom'
import { NUMBER_OF_ITEMS_PER_PAGE } from '../../constants'
import Pagination from '../../components/pagination'
import { RefreshLoader } from '../../components/spinner/refreshLoader'
import { Avatar } from '../../components/avatar'
import { ReportPeriodRange } from '../../redux-toolkits/reports/reports.type'
import Layout from '../../layout'

import { PageHeader } from '../../components/typography/PageHeader'
import { DateFilterNav } from '../../components/inputs/dateFilterNav'
import Dropdown from '../../components/inputs/dropdown'
import { Button } from '../../components/button'

const pageTabs = [{ name: 'App feedback', id: 'App feedback' }]

const FeedbackPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('App feedback')

  const [query, setQuery] = useState<QueryProps>({
    page: 1,
    limit: NUMBER_OF_ITEMS_PER_PAGE,
    sort: 'low_to_high',
  })

  const [searchParams, setSearchParams] = useSearchParams()

  const { data, isFetching, isLoading } = useGetAllRatingsQuery(
    {
      ...query,
    },
    {
      refetchOnMountOrArgChange: true,
    },
  )

  const handlePageChange = (page: number): void => {
    setSearchParams({
      ...Object.fromEntries(searchParams),
      sort: searchParams.get('sort') ?? '',
      page: `${page}`,
    })
  }

  useEffect(() => {
    const custDate = searchParams.get('isCustomDateRange')
      ? searchParams.get('isCustomDateRange') === 'true'
      : false

    setQuery({
      page: Number(searchParams.get('page') || 1),
      limit: NUMBER_OF_ITEMS_PER_PAGE,
      sort: searchParams.get('sort') || undefined,
      startDate: custDate
        ? searchParams.get('startDate') || undefined
        : undefined,
      endDate: custDate ? searchParams.get('endDate') || undefined : undefined,
      isCustomDateRange: custDate,
      filter: custDate
        ? undefined
        : (searchParams.get('filter') as ReportPeriodRange) || 'today',
    })
  }, [searchParams])

  return (
    <Layout>
      <div className="flex flex-col md:items-center md:flex-row md:justify-between gap-4 mb-10">
        <PageHeader title="Customers Feedback" className="capitalize !mb-0" />

        <div className="flex flex-col md:flex-row space-y-8 md:space-y-0">
          <DateFilterNav
            filter={query?.filter ?? 'custom'}
            isCustomDateRange={query?.isCustomDateRange ?? false}
            startDate={query?.startDate}
            endDate={query?.endDate}
            setFilter={(filter) => {
              setSearchParams({
                ...Object.fromEntries(searchParams),
                filter: filter.filter ?? '',
                isCustomDateRange: String(filter.isCustomDateRange),
                endDate: filter.endDate ?? '',
                startDate: filter.startDate ?? '',
                page: '1',
              })
            }}
          />
        </div>
      </div>

      <div className="flex flex-row justify-end">
        <Dropdown
          menuClassName=""
          menuButton={
            <Button
              label={`Sort: ${
                query?.sort === 'low_to_high' ? 'Low to high' : 'High to low'
              }`}
              primary={false}
              icon="filter"
              className="!font-normal ml-auto"
            />
          }
          menuItems={[
            { name: 'Low to high', value: 'low_to_high' },
            { name: 'High to low', value: 'high_to_low' },
          ].map((filter) => ({
            ...filter,
            onClick: () => {
              setSearchParams({
                ...Object.fromEntries(searchParams),
                sort: filter.value,
                page: '1',
              })
            },
          }))}
        />
      </div>

      <div>
        <CountCardContainer>
          <div
            className={`
            bg-transparent
            p-6
            py-4
            rounded-2xl
            border border-[#EAEAEA]
            flex flex-col gap-2 shadow-count-card
          `}
          >
            <div className="text-gray-500 flex flex-row items-center justify-between">
              <p className="text-base">Total Feedback</p>
              <Icon id="tool-tip-icon" hanging={20} width={20} />
            </div>
            <p className="text-2xl font-semibold ">
              {data?.data?.total} Feedback
            </p>
          </div>
        </CountCardContainer>

        <TabNav
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          tabs={pageTabs}
          className="mb-10"
        />

        {isFetching || isLoading ? (
          <RefreshLoader />
        ) : (
          <>
            {data?.data &&
              data?.data?.ratings.map((item) => (
                <div
                  key={item._id}
                  className={`
                    bg-transparent
                    p-6
                    py-4
                    rounded-2xl
                    border border-black
                    flex
                    flex-col
                    gap-2
                    shadow-count-card
                    w-full
                    lg:w-1/2
                    mb-4
                  `}
                >
                  {item?.userId && (
                    <div className="flex flex-row justify-between relative">
                      <div className="flex flex-row items-center space-x-3">
                        <Avatar
                          name={`${item?.userId?.firstname} ${item?.userId?.lastname}`}
                        />
                        <div>
                          <p className="text-base font-medium text-sec-black">
                            {capitalize(item?.userId?.firstname) +
                              ' ' +
                              capitalize(item?.userId?.lastname)}
                          </p>

                          <p className="text-sm text-sec-black">
                            {item?.userId?.phoneNumber}
                          </p>
                          <p className="text-sm text-sec-black text-clip">
                            {item?.userId?.email}
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 absolute right-0">
                        {format(new Date(item.createdAt), 'dd/MM/yyyy h:mma')}
                      </p>
                    </div>
                  )}

                  <Rating
                    count={5}
                    size={40}
                    rating={item.rating}
                    setRating={() => {}}
                    disabled={true}
                  />

                  <p className="text-base font-medium text-sec-black">
                    {item.feedbackText}
                  </p>
                </div>
              ))}

            {data?.data.total &&
            NUMBER_OF_ITEMS_PER_PAGE &&
            data?.data.total / NUMBER_OF_ITEMS_PER_PAGE > 1 ? (
              <Pagination
                currentPage={Number(data?.data.currentPage) ?? 0}
                totalCount={data?.data.total ?? 0}
                itemsPerPage={NUMBER_OF_ITEMS_PER_PAGE}
                pageRangeDisplayed={3}
                onPageChange={handlePageChange}
                className="mt-10  bottom-0 left-0 right-0"
              />
            ) : undefined}
          </>
        )}
      </div>
    </Layout>
  )
}

export default FeedbackPage
